import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSession } from "./SessionContext";
import { getCookie } from "../../utils/cookieUtil";
import { API_HOST } from "../../constants";
import Modal from "../ui/Modal";
import Button from "../ui/Button";

function Login() {
  const { setIsLoggedIn } = useSession();
  const navigate = useNavigate();

  const [showJoin, setShowJoin] = useState(false);
  const [showPwReset, setShowPwReset] = useState(false);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    if (!username) newErrors.username = "아이디를 입력해야 합니다.";
    if (!password) newErrors.password = "비밀번호를 입력해야 합니다.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleConfirm = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const csrfToken = getCookie("csrftoken");
      const response = await fetch(`${API_HOST}/api/login/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
        body: JSON.stringify({ username, password }),
        credentials: "include",
      });

      if (response.ok) {
        setIsLoggedIn(true);
        navigate("/");
      } else {
        alert("로그인에 실패했습니다. 아이디/비밀번호를 확인해주세요.");
      }
    } catch (error) {
      alert("서버 오류가 발생했습니다. 다시 시도해주세요.");
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-12">
      <div className="glass-card w-full max-w-lg p-8">
        <div className="mb-6 text-center">
          <p className="text-xs uppercase tracking-[0.4em] text-ink-200">Community OS</p>
          <h1 className="mt-3 font-display text-3xl text-white">썰모</h1>
          <p className="mt-2 text-sm text-ink-200">세상의 모든 이야기</p>
        </div>

        <form onSubmit={handleConfirm} className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-ink-200">ID</label>
            <input
              type="text"
              className="soft-input mt-2"
              placeholder="아이디를 입력하세요"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
            {errors.username && <p className="mt-1 text-xs text-red-200">{errors.username}</p>}
          </div>
          <div>
            <label className="text-xs font-semibold text-ink-200">비밀번호</label>
            <input
              type="password"
              className="soft-input mt-2"
              placeholder="비밀번호를 입력하세요"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {errors.password && <p className="mt-1 text-xs text-red-200">{errors.password}</p>}
          </div>
          <Button className="w-full" disabled={loading}>
            {loading ? "로그인 중..." : "로그인"}
          </Button>
        </form>

        <div className="mt-6 flex flex-col gap-2 text-sm text-ink-200 sm:flex-row sm:items-center sm:justify-between">
          <button type="button" className="underline-offset-4 hover:underline" onClick={() => setShowJoin(true)}>
            회원가입
          </button>
          <button type="button" className="underline-offset-4 hover:underline" onClick={() => setShowPwReset(true)}>
            비밀번호 초기화
          </button>
        </div>

        <p className="mt-6 text-center text-xs text-ink-200">ⓒ SSULMO. All Rights Reserved.</p>
      </div>

      <JoinModal open={showJoin} onClose={() => setShowJoin(false)} />
      <PasswordResetModal open={showPwReset} onClose={() => setShowPwReset(false)} />
    </div>
  );
}

function JoinModal({ open, onClose }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [email, setEmail] = useState("");
  const [nickname, setNickname] = useState("");
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!username) newErrors.username = "아이디를 입력해야 합니다.";
    if (!password) newErrors.password = "비밀번호를 입력해야 합니다.";
    if (!confirmPassword) {
      newErrors.confirmPassword = "비밀번호를 입력해야 합니다.";
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = "입력한 비밀번호가 맞지 않습니다.";
    }
    if (!nickname) newErrors.nickname = "별명을 입력해야 합니다.";
    if (!email) newErrors.email = "이메일을 입력해야 합니다.";
    if (email && !/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "올바른 이메일 형식이 아닙니다.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setUsername("");
    setPassword("");
    setConfirmPassword("");
    setEmail("");
    setNickname("");
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleConfirm = async () => {
    if (!validateForm()) return;

    const newUser = { username, password, nickname, email };

    try {
      const csrfToken = getCookie("csrftoken");
      const response = await fetch(`${API_HOST}/api/register/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
        body: JSON.stringify(newUser),
        credentials: "include",
      });

      if (response.ok) {
        handleClose();
      } else {
        const data = await response.json();
        alert(data.message || "Registration failed.");
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="회원가입"
      description="간단한 정보로 썰모에 참여하세요."
      footer={
        <>
          <Button variant="secondary" onClick={handleClose}>
            취소
          </Button>
          <Button onClick={handleConfirm}>확인</Button>
        </>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="text-xs font-semibold text-ink-200">ID</label>
          <input className="soft-input mt-2" value={username} onChange={(e) => setUsername(e.target.value)} />
          {errors.username && <p className="mt-1 text-xs text-red-200">{errors.username}</p>}
        </div>
        <div>
          <label className="text-xs font-semibold text-ink-200">별명</label>
          <input className="soft-input mt-2" value={nickname} onChange={(e) => setNickname(e.target.value)} />
          {errors.nickname && <p className="mt-1 text-xs text-red-200">{errors.nickname}</p>}
        </div>
      </div>
      <div>
        <label className="text-xs font-semibold text-ink-200">이메일</label>
        <input className="soft-input mt-2" value={email} onChange={(e) => setEmail(e.target.value)} />
        {errors.email && <p className="mt-1 text-xs text-red-200">{errors.email}</p>}
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="text-xs font-semibold text-ink-200">비밀번호</label>
          <input type="password" className="soft-input mt-2" value={password} onChange={(e) => setPassword(e.target.value)} />
          {errors.password && <p className="mt-1 text-xs text-red-200">{errors.password}</p>}
        </div>
        <div>
          <label className="text-xs font-semibold text-ink-200">비밀번호 확인</label>
          <input
            type="password"
            className="soft-input mt-2"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
          {errors.confirmPassword && <p className="mt-1 text-xs text-red-200">{errors.confirmPassword}</p>}
        </div>
      </div>
    </Modal>
  );
}

function PasswordResetModal({ open, onClose }) {
  const [username, setUsername] = useState("");
  const [fullname, setFullname] = useState("");
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!username) newErrors.username = "아이디를 입력해야 합니다.";
    if (!fullname) newErrors.fullname = "이름을 입력해야 합니다.";
    if (!email) newErrors.email = "이메일을 입력해야 합니다.";
    if (email && !/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "올바른 이메일 형식이 아닙니다.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setUsername("");
    setFullname("");
    setEmail("");
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleConfirm = async () => {
    if (!validateForm()) return;

    const confirmUserInfo = {
      username,
      full_name: fullname,
      email,
    };

    try {
      const csrfToken = getCookie("csrftoken");
      const response = await fetch(`${API_HOST}/api/password-reset/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
        body: JSON.stringify(confirmUserInfo),
        credentials: "include",
      });
      if (response.ok) {
        handleClose();
      } else {
        alert("비밀번호 초기화에 실패했습니다. 정보를 확인해주세요.");
      }
    } catch (error) {
      alert("서버 오류가 발생했습니다. 다시 시도해주세요.");
      console.error("Error:", error);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="비밀번호 초기화"
      description="등록된 이메일로 임시 비밀번호를 전송합니다."
      footer={
        <>
          <Button variant="secondary" onClick={handleClose}>
            취소
          </Button>
          <Button onClick={handleConfirm}>확인</Button>
        </>
      }
    >
      <div>
        <label className="text-xs font-semibold text-ink-200">ID</label>
        <input className="soft-input mt-2" value={username} onChange={(e) => setUsername(e.target.value)} />
        {errors.username && <p className="mt-1 text-xs text-red-200">{errors.username}</p>}
      </div>
      <div>
        <label className="text-xs font-semibold text-ink-200">이름</label>
        <input className="soft-input mt-2" value={fullname} onChange={(e) => setFullname(e.target.value)} />
        {errors.fullname && <p className="mt-1 text-xs text-red-200">{errors.fullname}</p>}
      </div>
      <div>
        <label className="text-xs font-semibold text-ink-200">이메일</label>
        <input className="soft-input mt-2" value={email} onChange={(e) => setEmail(e.target.value)} />
        {errors.email && <p className="mt-1 text-xs text-red-200">{errors.email}</p>}
      </div>
    </Modal>
  );
}

export default Login;
